// The two complete Seller Sourcing History pages (real pages, not giant Swipers):
//   /seller/provider-history/providers   Recently Viewed Providers           (max 15)
//   /seller/provider-history/products    Recently Viewed Provider Products   (max 30)
// Same cards as the Swipers (ProviderShowcaseCard, ProviderProductCard variant="history").
// Newest first; the caps are enforced server-side, so no pagination.
import React from "react";
import { Link } from "react-router-dom";
import { FaStore, FaShoppingBag } from "react-icons/fa";
import Layout from "../../Layout/Layout";
import { elakayAutoText } from "../../pageInside/dataElakay";
import DropshippingInterneLayout from "../components/DropshippingInterneLayout";
import ProviderShowcaseCard, { ProviderShowcaseSkeleton } from "../ui/ProviderShowcaseCard";
import ProviderProductCard, { ProviderProductCardSkeleton } from "../components/ProviderProductCard";
import { HistorySectionHeader } from "./HistorySwiperSection";
import { SELLER_HISTORY_ROUTES, SELLER_HISTORY_MAX } from "./sellerHistoryRoutes";
import { canUseSellerSourcingHistory, historyProducts, useSellerSourcingHistory } from "./useSellerSourcingHistory";
import { useProviderLikeToggle } from "./useProviderLikeToggle";
import "../seller/DropshippingIn.css";
import "../seller/SellerProviderMarketplace.css";
import "../ui/ui.css";
import "./sellerHistory.css";

const text = elakayAutoText;

const lastViewed = (date) => {
  const d = date ? new Date(date) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return text("h_sph_last_viewed", "Viewed {value0}", { value0: d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) });
};

const PAGES = {
  providers: {
    icon: FaStore,
    title: () => text("h_sph_providers_title", "Recently Viewed Providers"),
    subtitle: () => text("h_sph_providers_page_sub", "Providers you recently visited while sourcing products."),
    counter: (n, max) => text("h_sph_providers_count", "{value0} of {value1} recently viewed providers", { value0: n, value1: max }),
  },
  products: {
    icon: FaShoppingBag,
    title: () => text("h_sph_products_title", "Recently Viewed Provider Products"),
    subtitle: () => text("h_sph_products_page_sub", "Products you recently viewed while exploring providers."),
    counter: (n, max) => text("h_sph_products_count", "{value0} of {value1} recently viewed products", { value0: n, value1: max }),
  },
};

function HistoryShell({ infoSource, kind, count, loading, children }) {
  const page = PAGES[kind];
  const max = SELLER_HISTORY_MAX[kind];
  return (
    <Layout infoSource={infoSource} titleElakay="providerProducerIn" SliderPunShow={false} loading={false} outOffPage={false} {...infoSource?.allSettings?.layout} showFooter={true}>
      <DropshippingInterneLayout infoSource={infoSource}>
        <div className="dsi-page sph-page">
          <nav className="dsi-breadcrumb" aria-label={text("h_c766e665183df1", "Breadcrumb")}>
            <Link to="/seller/providers">{text("h_spm_title", "Providers & Producers")}</Link><span aria-hidden="true">›</span>
            <span aria-current="page">{text("h_sph_history", "History")}</span>
          </nav>
          <div className="sph-page__hero">
            <HistorySectionHeader icon={page.icon} level={1} title={page.title()} subtitle={page.subtitle()} />
            <span className="sph-page__counter" aria-live="polite">
              {loading ? text("h_sph_max", "{value0} maximum", { value0: max }) : page.counter(count, max)}
            </span>
          </div>
          <nav className="sph-page__switch" aria-label={text("h_sph_history", "History")}>
            <Link to={SELLER_HISTORY_ROUTES.providers} aria-current={kind === "providers" ? "page" : undefined}>{text("h_sph_tab_providers", "Viewed Providers")}</Link>
            <Link to={SELLER_HISTORY_ROUTES.products} aria-current={kind === "products" ? "page" : undefined}>{text("h_sph_tab_products", "Viewed Products")}</Link>
          </nav>
          {children}
        </div>
      </DropshippingInterneLayout>
    </Layout>
  );
}

const ErrorCard = ({ onRetry }) => (
  <div className="dsi-state-card" role="alert">
    <h2>{text("h_spm_error_title", "Something went wrong")}</h2>
    <p>{text("h_sph_error_short", "Unable to load recently viewed items.")}</p>
    <button type="button" className="dsi-state-card__btn" onClick={onRetry}>{text("h_spm_retry", "Retry")}</button>
  </div>
);

const EmptyState = ({ icon: Icon, title, message, cta, to }) => (
  <div className="sph-empty">
    <span className="sph-empty__icon" aria-hidden="true"><Icon /></span>
    <h2>{title}</h2>
    <p>{message}</p>
    <Link to={to}>{cta}</Link>
  </div>
);

const Busy = ({ className, count, Skeleton }) => (
  <div className={className} role="status" aria-busy="true">
    <span className="spm-sr-only">{text("h_spm_loading", "Loading…")}</span>
    {Array.from({ length: count }, (_, i) => <Skeleton key={i} />)}
  </div>
);

export function ProviderHistoryPage({ infoSource }) {
  const enabled = canUseSellerSourcingHistory(infoSource);
  const { items, loading, error, reload, setItems } = useSellerSourcingHistory("providers", { enabled });
  const like = useProviderLikeToggle(setItems);
  let body;
  if (error) body = <ErrorCard onRetry={reload} />;
  else if (loading && !items.length) body = <Busy className="sph-provider-grid" count={6} Skeleton={ProviderShowcaseSkeleton} />;
  else if (!items.length) body = <EmptyState icon={FaStore} title={text("h_sph_providers_empty_title", "No providers viewed yet")} message={text("h_sph_providers_empty", "Providers you visit will appear here.")} cta={text("h_sph_browse_providers", "Browse Providers")} to="/seller/providers" />;
  else body = (
    <div className="sph-provider-grid">
      {items.map((entry) => (
        <div key={String(entry.providerId)} className="sph-provider-grid__item">
          <ProviderShowcaseCard entry={entry} onToggleLike={like.toggle} liking={like.isBusy(entry)} />
          {lastViewed(entry.lastViewedAt) && <p className="sph-since">{lastViewed(entry.lastViewedAt)}</p>}
        </div>
      ))}
    </div>
  );
  return <HistoryShell infoSource={infoSource} kind="providers" count={items.length} loading={loading}>{body}</HistoryShell>;
}

export function ProviderProductHistoryPage({ infoSource }) {
  const enabled = canUseSellerSourcingHistory(infoSource);
  const { items, loading, error, reload } = useSellerSourcingHistory("products", { enabled });
  const products = historyProducts(items);
  let body;
  if (error) body = <ErrorCard onRetry={reload} />;
  else if (loading && !products.length) body = <Busy className="pgrid" count={10} Skeleton={ProviderProductCardSkeleton} />;
  else if (!products.length) body = <EmptyState icon={FaShoppingBag} title={text("h_sph_products_empty_title", "No provider products viewed yet")} message={text("h_sph_products_empty", "Products you explore from providers will appear here.")} cta={text("h_sph_explore_products", "Explore Provider Products")} to="/seller/provider-catalog" />;
  else body = <div className="pgrid">{products.map((p) => <ProviderProductCard key={String(p._id)} variant="history" product={p} />)}</div>;
  return <HistoryShell infoSource={infoSource} kind="products" count={products.length} loading={loading}>{body}</HistoryShell>;
}
